/**
 * 路由表。
 *
 * 用 HashRouter 而非 BrowserRouter：本项目随安装包分发，用户可能以
 * file:// 或任意子路径下的静态服务打开构建产物，hash 路由不依赖服务端 rewrite，
 * 少一类"刷新 404"的部署故障（旧版在 Nginx 配置上就踩过同类坑）。
 *
 * 路由结构由 nav.ts 推导，新增页面只改 nav.ts 一处。
 */
import type { ReactElement } from 'react';
import { createHashRouter, Navigate } from 'react-router-dom';

import { CanvasPage } from './pages/CanvasPage';
import { CanvasEditorPage } from './pages/CanvasEditorPage';
import { AssetLibraryPage } from './pages/AssetLibraryPage';
import { GenerationStudioPage } from './pages/GenerationStudioPage';
import { AgentPage } from './pages/AgentPage';
import { TeamSpacePage } from './pages/TeamSpacePage';
import { StoryboardPage } from './pages/StoryboardPage';
import { EditingPage } from './pages/EditingPage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import { DirectorWorkbenchPage } from './pages/DirectorWorkbenchPage';
import { SettingsPage } from './pages/SettingsPage';
import { HomeWorkbenchPage } from './pages/HomeWorkbenchPage';
import { LoginPage } from './pages/LoginPage';
import { PricingPage } from './pages/PricingPage';
import { GlobalLayout } from './layouts/GlobalLayout';
import { WorkbenchLayout } from './layouts/WorkbenchLayout';
import { NAV_ITEMS } from './nav';

/** 已交付页面在此显式登记，其余一律走占位页 */
function pageFor(path: string): ReactElement {
  switch (path) {
    case '/':
      return <HomeWorkbenchPage />;
    case '/canvas':
      return <CanvasPage />;
    case '/assets':
      return <AssetLibraryPage />;
    case '/generation':
      return <GenerationStudioPage />;
    case '/agent':
      return <AgentPage />;
    case '/team':
      return <TeamSpacePage />;
    case '/storyboard':
      return <StoryboardPage />;
    case '/editing':
      return <EditingPage />;
    default:
      return <ComingSoonPage path={path} />;
  }
}

export const router = createHashRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <GlobalLayout />,
    children: [
      ...NAV_ITEMS.map((item) => ({
        id: item.key,
        path: item.path,
        element: pageFor(item.path),
      })),
      { path: '/diagnostics', element: <DiagnosticsPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/canvas/editor', element: <CanvasEditorPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  {
    element: <WorkbenchLayout title="3D 导演台" />,
    children: [{ path: '/director', element: <DirectorWorkbenchPage /> }],
  },
]);
